/**
 * Airforce Performance — Engine Package Builder
 * File: assets/airforce-builder.js
 *
 * Dependency-free vanilla JS. One asset file (Shopify's assets folder is flat and
 * cache-busts each file independently), organised into small single-purpose modules:
 *
 *   Util       – DOM + formatting helpers
 *   Money      – Shopify money_format based formatting (Markets-aware)
 *   Analytics  – CustomEvents + dataLayer + Shopify customer events
 *   Catalog    – normalises the Liquid-rendered JSON payload
 *   Rules      – configuration logic (included / optional / dependencies / groups)
 *   Pricing    – build totals (sum-of-products or Shopify bundle product)
 *   Dyno       – honest dyno-run matching (never shows a superset test)
 *   CartApi    – Shopify AJAX cart with rollback
 *   AirforceBuilder – controller: state, rendering, URL state, cart flow
 *
 * SECURITY MODEL
 *   This script never decides what anyone is charged. It sends variant IDs and
 *   quantities to Shopify's cart. Displayed prices come from Liquid, rendered by
 *   Shopify for the current customer context (market currency, B2B catalog).
 *   Shopify (product prices, B2B catalogs, bundle products, discount functions)
 *   enforces the price at checkout. Editing this file in a browser cannot change that.
 */
(() => {
  'use strict';

  if (window.AirforceBuilder && window.AirforceBuilder.version) return;

  const VERSION = '1.1.0';
  const DESKTOP_QUERY = '(min-width: 990px)';
  const IMG_WIDTHS_CARD = [180, 360, 540, 720];
  const IMG_WIDTHS_WIDE = [480, 720, 960, 1200, 1600];

  const DEFAULT_STRINGS = {
    choosePlatform: 'Choose an engine platform to begin.',
    chooseStage: 'Select a stage to continue.',
    noStages: 'Stages for this platform are coming soon.',
    noOptions: 'No optional upgrades are offered for this platform yet.',
    selectStageFirst: 'Select a stage',
    select: 'Select',
    selected: 'Selected',
    included: 'Included',
    includedWith: 'Included with {stage}',
    availableWith: 'Available with {stages}',
    notOffered: 'Not offered with {stage}',
    recommended: 'Recommended',
    add: 'Add',
    added: 'Added',
    addAria: 'Add {title} to build',
    details: 'Details',
    performanceNotes: 'Performance notes',
    installationNotes: 'Installation notes',
    outOfStock: 'Out of stock',
    backorder: 'Backorder',
    backorderLong: 'Available on backorder',
    unavailable: 'Unavailable',
    temporarilyUnavailable: 'Temporarily unavailable',
    requiresUnavailable: '{title} requires {items}, which is not available with this stage.',
    conflictsIncluded: 'Not compatible with {items}, which is already included in this stage.',
    conflictGroup: '{items} cannot be combined in one build.',
    stageUnavailable: '{stage} is temporarily unavailable: {items} currently unavailable.',
    stageMisconfigured: 'This package is not currently available online. Please contact us.',
    requiredGroup: 'Choose a {group} option to complete this build.',
    replaced: '{removed} was replaced by {added}.',
    autoAdded: '{added} was added — required by {by}.',
    autoRemoved: '{removed} was removed — it requires {requires}.',
    droppedOnStage: '{items} not available with {stage} and removed from your build.',
    backorderWarning: '{items} will ship on backorder.',
    dealerMissing: 'No dealer price is set for {items}. The standard price is shown and is what will be charged.',
    dealerCurrency: 'Dealer prices are available in {currency} only. Standard prices are shown.',
    dealerPricing: 'Dealer pricing',
    msrp: 'MSRP',
    packageLabel: '{stage} package',
    packageComponents: '{count} components',
    packageSavings: 'Package savings',
    value: 'Value {price}',
    total: 'Total',
    includedTitle: 'Your {stage} package',
    dynoExact: 'Dyno shown represents this exact configuration: {tested}.',
    dynoPartial: 'Dyno shown represents {tested}. Selected options not represented in this test: {missing}.',
    dynoNone: 'No dyno test has been published for this configuration yet, so no performance figures are shown.',
    dynoNoStage: 'Select a stage to see dyno results.',
    dynoGraphMissing: 'Dyno graph image not available for this test.',
    yourBuild: 'Your build',
    testedConfig: 'Tested setup',
    stock: 'Stock',
    gain: 'Gain',
    hp: 'HP',
    rpm: 'RPM',
    peakGain: 'Peak HP gain',
    torqueGain: 'Peak torque gain',
    peakPowerRpm: 'Peak power',
    configuration: 'Configuration',
    basedOn: 'Figures from tested setup: {tested}.',
    notTested: 'Not yet dyno tested',
    testConfiguration: 'Test configuration',
    testBike: 'Test bike',
    fuel: 'Fuel',
    dynoType: 'Dyno',
    testDate: 'Test date',
    conditions: 'Conditions',
    peakHpAt: 'Peak power at',
    peakTqAt: 'Peak torque at',
    enlargeGraph: 'Enlarge dyno graph',
    addToCart: 'Add build to cart',
    adding: 'Adding build…',
    checking: 'Checking availability…',
    addedToCart: 'Build added to cart.',
    cartError: 'This build wasn’t added to your cart. {reason} Nothing from this build was left in your cart.',
    cartRollbackFailed: 'This build wasn’t fully added. {reason} Please check your cart — some items may have been added.',
    pricesChanged: 'Prices or availability changed since this page loaded. Your build has been updated — review it before adding to cart.',
    linkCopied: 'Build link copied.',
    linkCopyFailed: 'Copy this link to share your build: {url}',
    urlInvalid: 'Part of this shared build link couldn’t be restored: {items}. The closest valid build is shown.',
    urlPlatform: 'platform “{key}”',
    urlStage: 'stage “{key}”',
    urlOption: '“{key}”',
    genericError: 'The builder hit an unexpected problem. Refresh the page to continue.',
    priceUnavailable: 'Price unavailable',
    requirementNeeded: 'Choose an option for: {items}.',
    alreadyHave: 'Already have one',
    alreadyHaveAria: 'I already have: {item}',
    requirementIncluded: '{title} is included with {stage}.',
    suppliedByYou: 'Supplied by you',
    ownedProperty: 'Customer already has: {item}',
    fitsFrom: 'Fits {from} and newer',
    fitsRange: 'Fits {from}–{to}',
    fitsTo: 'Fits {to} and older',
    viewProduct: 'View product'
  };

  /* ------------------------------------------------------------------ *
   * Util
   * ------------------------------------------------------------------ */
  const Util = {
    qs(sel, root = document) { return root.querySelector(sel); },
    qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); },
    slot(root, name) { return root ? root.querySelector(`[data-slot="${name}"]`) : null; },
    uid() {
      const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
      return `AF-${Date.now().toString(36).toUpperCase()}-${rand}`;
    },
    template(str, vars = {}) {
      return String(str == null ? '' : str).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
    },
    safeLocale(locale) {
      if (!locale) return undefined;
      try {
        const supported = Intl.NumberFormat.supportedLocalesOf([locale]);
        return supported.length ? supported[0] : undefined;
      } catch (e) {
        return undefined;
      }
    },
    list(items, locale) {
      const arr = (items || []).filter(Boolean);
      if (!arr.length) return '';
      try {
        if (typeof Intl.ListFormat === 'function') {
          return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(arr);
        }
      } catch (e) { /* fall through */ }
      return arr.join(', ');
    },
    num(v) {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    formatNumber(n, locale, maxDigits = 1) {
      if (n === null || n === undefined) return '';
      try {
        return new Intl.NumberFormat(locale, { maximumFractionDigits: maxDigits }).format(n);
      } catch (e) {
        return String(Math.round(n * 10) / 10);
      }
    },
    imageUrl(src, width) {
      if (!src) return '';
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (e) {
        return src;
      }
    },
    srcset(src, widths) {
      return widths.map((w) => `${Util.imageUrl(src, w)} ${w}w`).join(', ');
    },
    stripTags(html) {
      const d = document.createElement('div');
      d.innerHTML = String(html || '');
      return d.textContent || '';
    },
    humanize(key) {
      const s = String(key || '').replace(/[_-]+/g, ' ').trim();
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    },
    reducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    },
    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined && text !== null) node.textContent = text;
      return node;
    }
  };

  /* ------------------------------------------------------------------ *
   * Money — uses the shop's own money_format from Liquid. When the buyer
   * is browsing in a different Markets currency, Intl formatting is used
   * for that currency (the amounts themselves are already converted by Shopify).
   * ------------------------------------------------------------------ */
  const Money = {
    create(cfg = {}) {
      const locale = Util.safeLocale(cfg.locale);
      const fmt = Util.stripTags(cfg.format || '${{amount}}');
      const fmtCurrency = Util.stripTags(cfg.formatWithCurrency || cfg.format || '${{amount}}');
      let intl = null;
      if (cfg.currency && cfg.shopCurrency && cfg.currency !== cfg.shopCurrency) {
        try { intl = new Intl.NumberFormat(locale, { style: 'currency', currency: cfg.currency }); } catch (e) { intl = null; }
      }
      const delimit = (cents, precision, thousands = ',', decimal = '.') => {
        const negative = cents < 0;
        const fixed = (Math.abs(cents) / 100).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return (negative ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
      };
      const apply = (cents, format) => {
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return format;
        let value;
        switch (match[1]) {
          case 'amount_no_decimals': value = delimit(cents, 0); break;
          case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
          case 'amount_with_apostrophe_separator': value = delimit(cents, 2, "'", '.'); break;
          case 'amount_no_decimals_with_space_separator': value = delimit(cents, 0, ' ', ','); break;
          case 'amount_with_space_separator': value = delimit(cents, 2, ' ', ','); break;
          case 'amount_with_period_and_space_separator': value = delimit(cents, 2, ' ', '.'); break;
          case 'amount':
          default: value = delimit(cents, 2);
        }
        return format.replace(match[0], value);
      };
      const clean = (cents) => {
        const n = Number(cents);
        return Number.isFinite(n) ? Math.round(n) : 0;
      };
      return {
        format: (cents) => (intl ? intl.format(clean(cents) / 100) : apply(clean(cents), fmt)),
        formatWithCurrency: (cents) => (intl ? `${intl.format(clean(cents) / 100)} ${cfg.currency}` : apply(clean(cents), fmtCurrency))
      };
    }
  };

  /* ------------------------------------------------------------------ *
   * Analytics — vendor-neutral. Listen with:
   *   document.addEventListener('airforce:builder', e => console.log(e.detail))
   * ------------------------------------------------------------------ */
  const Analytics = {
    create(root, context) {
      const once = new Set();
      return {
        track(name, data = {}, onceKey = null) {
          try {
            if (onceKey) {
              if (once.has(onceKey)) return;
              once.add(onceKey);
            }
            const detail = Object.assign({ name, timestamp: Date.now() }, context, data);
            root.dispatchEvent(new CustomEvent(`airforce:${name}`, { bubbles: true, detail }));
            root.dispatchEvent(new CustomEvent('airforce:builder', { bubbles: true, detail }));
            if (Array.isArray(window.dataLayer)) {
              window.dataLayer.push({ event: name, airforce_builder: detail });
            }
            if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
              window.Shopify.analytics.publish(`airforce_${name}`, detail);
            }
          } catch (e) {
            console.warn('[AirforceBuilder] analytics error', e);
          }
        }
      };
    }
  };

  /* ------------------------------------------------------------------ *
   * Catalog — normalises and indexes the Liquid payload.
   * ------------------------------------------------------------------ */
  function createCatalog(raw) {
    const data = raw || {};
    const products = data.products && typeof data.products === 'object' ? data.products : {};
    const stages = data.stages && typeof data.stages === 'object' ? data.stages : {};
    const platforms = (Array.isArray(data.platforms) ? data.platforms : []).filter((p) => p && p.key && p.name);

    Object.values(products).forEach((p) => {
      const price = Util.num(p.price);
      p.priceKnown = price !== null;
      p.price = price === null ? 0 : price;
      p.msrp = Util.num(p.msrp);
      p.compareAt = Util.num(p.compareAt);
      p.qty = Math.max(1, Util.num(p.qty) || 1);
      p.requires = (Array.isArray(p.requires) ? p.requires : []).filter((h) => h && h !== p.handle);
      p.sort = Util.num(p.sort) === null ? 999 : Util.num(p.sort);
      p.title = p.title || p.handle;
      p.fitFrom = Util.num(p.fitFrom);
      p.fitTo = Util.num(p.fitTo);
    });

    const requirements = data.requirements && typeof data.requirements === 'object' ? data.requirements : {};
    Object.values(requirements).forEach((r) => {
      r.products = (Array.isArray(r.products) ? r.products : []).filter((h) => products[h]);
      r.other = (Array.isArray(r.other) ? r.other : []).filter((o) => o && o.key && o.label);
      r.allowOwned = r.allowOwned !== false;
      r.name = r.name || r.key || r.id;
      r.key = String(r.key || r.id).toLowerCase();
    });

    const onlyKnown = (arr) => (Array.isArray(arr) ? arr : []).filter((h) => products[h]);
    Object.values(stages).forEach((s) => {
      s.included = onlyKnown(s.included);
      s.optional = onlyKnown(s.optional).filter((h) => !s.included.includes(h));
      s.defaults = onlyKnown(s.defaults).filter((h) => s.optional.includes(h));
      s.recommended = onlyKnown(s.recommended);
      s.requiredGroups = (Array.isArray(s.requiredGroups) ? s.requiredGroups : []).filter(Boolean);
      s.issues = Array.isArray(s.issues) ? s.issues : [];
      s.requirements = (Array.isArray(s.requirements) ? s.requirements : []).filter((id) => requirements[id]);
      s.pricingMode = s.pricingMode === 'bundle_product' ? 'bundle_product' : 'sum_of_products';
      s.name = s.name || s.key || s.id;
    });

    const byKey = new Map();
    const stagePlatform = new Map();
    platforms.forEach((p) => {
      p.stages = (Array.isArray(p.stages) ? p.stages : []).filter((id) => stages[id]);
      byKey.set(String(p.key).toLowerCase(), p);
      p.stages.forEach((id) => { if (!stagePlatform.has(id)) stagePlatform.set(id, p.key); });
    });

    const productByKey = new Map();
    Object.values(products).forEach((p) => {
      if (p.key) productByKey.set(String(p.key).toLowerCase(), p);
      productByKey.set(String(p.handle).toLowerCase(), p);
    });

    const dynoByStage = new Map();
    (Array.isArray(data.dyno) ? data.dyno : []).forEach((run) => {
      if (!run || !run.stage || !stages[run.stage]) return;
      run.tested = (Array.isArray(run.tested) ? run.tested : []).filter((h) => products[h]);
      ['stockHp', 'stockTq', 'hp', 'tq', 'hpRpm', 'tqRpm', 'priority'].forEach((k) => { run[k] = Util.num(run[k]); });
      if (!dynoByStage.has(run.stage)) dynoByStage.set(run.stage, []);
      dynoByStage.get(run.stage).push(run);
    });

    return {
      platforms,
      platform: (key) => (key ? byKey.get(String(key).toLowerCase()) || null : null),
      stage: (id) => (id && stages[id]) || null,
      product: (handle) => (handle && products[handle]) || null,
      productByKey: (key) => (key ? productByKey.get(String(key).toLowerCase()) || null : null),
      stagesFor: (platform) => (platform ? platform.stages.map((id) => stages[id]).filter(Boolean) : []),
      stageByKey(platform, key) {
        if (!platform || !key) return null;
        const k = String(key).toLowerCase();
        return this.stagesFor(platform).find((s) => String(s.key).toLowerCase() === k || s.id === key) || null;
      },
      platformOfStage: (id) => stagePlatform.get(id) || null,
      requirement: (id) => (id && requirements[id]) || null,
      requirementsFor: (stage) => (stage ? stage.requirements.map((id) => requirements[id]).filter(Boolean) : []),
      requirementProductsFor(platform) {
        const set = new Set();
        this.stagesFor(platform).forEach((s) => this.requirementsFor(s).forEach((r) => r.products.forEach((h) => set.add(h))));
        return set;
      },
      dynoRuns: (stageId) => dynoByStage.get(stageId) || []
    };
  }

  /* ------------------------------------------------------------------ *
   * Rules — pure configuration logic. No DOM.
   * ------------------------------------------------------------------ */
  const Rules = {
    /** Fitment of a stage = the narrowest model-year range of its included parts. */
    stageFitment(catalog, stage) {
      let from = null;
      let to = null;
      (stage ? stage.included : []).forEach((h) => {
        const p = catalog.product(h);
        if (!p) return;
        if (p.fitFrom !== null) from = from === null ? p.fitFrom : Math.max(from, p.fitFrom);
        if (p.fitTo !== null) to = to === null ? p.fitTo : Math.min(to, p.fitTo);
      });
      return { from, to };
    },

    /**
     * A build requirement (e.g. "ECU") is met when one of its products is included in the
     * stage, one is selected to buy, or the customer says they already have one.
     */
    requirementState(catalog, stage, req, options, owned) {
      const included = req.products.find((h) => stage.included.includes(h));
      if (included) return { kind: 'included', handle: included };
      const bought = req.products.find((h) => options.has(h));
      if (bought) return { kind: 'buy', handle: bought };
      const own = owned && owned.get(req.id);
      if (own) return { kind: 'own', own };
      return { kind: 'unanswered' };
    },

    /** Choices the customer can make for a requirement in this stage. */
    requirementChoices(catalog, stage, req) {
      const buy = req.products.filter((h) => stage.optional.includes(h) && catalog.product(h));
      const own = [];
      if (req.allowOwned) {
        req.products.forEach((h) => {
          const p = catalog.product(h);
          if (p) own.push({ value: `own:product:${h}`, type: 'product', handle: h, key: p.key || h, label: p.title });
        });
        req.other.forEach((o) => own.push({ value: `own:other:${o.key}`, type: 'other', key: o.key, label: o.label }));
      }
      return { buy, own };
    },

    ownedLabel(catalog, own) {
      if (!own) return '';
      if (own.type === 'product') return (catalog.product(own.handle) || {}).title || own.handle;
      return own.label || own.key;
    },

    stageStatus(catalog, stage) {
      const empty = { state: 'invalid', blocking: [], backorder: [] };
      if (!stage) return empty;
      if (stage.issues.includes('missing_included') || stage.issues.includes('missing_bundle')) return empty;
      const included = stage.included.map((h) => catalog.product(h)).filter(Boolean);
      const bundle = stage.pricingMode === 'bundle_product' ? catalog.product(stage.bundle) : null;
      if (!included.length) return empty;
      if (stage.pricingMode === 'bundle_product' && !bundle) return empty;
      const blocking = included.filter((p) => !p.available || p.builderEnabled === false || !p.priceKnown);
      if (bundle && (!bundle.available || !bundle.priceKnown)) blocking.push(bundle);
      const backorder = included.filter((p) => p.available && p.backorder);
      if (blocking.length) return { state: 'unavailable', blocking, backorder };
      const isBackorder = backorder.length > 0 || !!(bundle && bundle.backorder);
      return { state: isBackorder ? 'backorder' : 'available', blocking, backorder };
    },

    /** Every optional product offered anywhere on this platform, in display order. */
    optionHandles(catalog, platform, { includeRequirements = false } = {}) {
      const handles = new Set();
      const reqProducts = includeRequirements ? new Set() : catalog.requirementProductsFor(platform);
      catalog.stagesFor(platform).forEach((s) => s.optional.forEach((h) => { if (!reqProducts.has(h)) handles.add(h); }));
      return Array.from(handles)
        .map((h) => catalog.product(h))
        .filter((p) => p && p.builderEnabled !== false)
        .sort((a, b) => (a.sort - b.sort) || a.title.localeCompare(b.title))
        .map((p) => p.handle);
    },

    optionStatus(catalog, platform, stage, handle, options) {
      const product = catalog.product(handle);
      if (!product) return { kind: 'blocked', reasonKey: 'unavailable', items: [] };
      if (!stage) return { kind: 'no-stage' };
      if (stage.included.includes(handle)) return { kind: 'included' };
      if (!stage.optional.includes(handle)) {
        const where = catalog.stagesFor(platform).filter((s) => s.id !== stage.id && (s.optional.includes(handle) || s.included.includes(handle)));
        return { kind: 'other-stage', stages: where };
      }
      if (!product.available || !product.priceKnown || product.builderEnabled === false) return { kind: 'out-of-stock' };
      if (product.group) {
        const clash = stage.included.map((h) => catalog.product(h)).find((p) => p && p.group === product.group);
        if (clash) return { kind: 'blocked', reasonKey: 'conflictsIncluded', items: [clash.title] };
      }
      const unmet = [];
      product.requires.forEach((r) => {
        if (stage.included.includes(r)) return;
        const rp = catalog.product(r);
        if (!rp || !stage.optional.includes(r) || !rp.available || !rp.priceKnown) unmet.push(rp ? rp.title : r);
      });
      if (unmet.length) return { kind: 'blocked', reasonKey: 'requiresUnavailable', items: unmet, title: product.title };
      return { kind: 'available', selected: options.has(handle), backorder: !!product.backorder };
    },

    pruneDependents(catalog, stage, next, notices, t, list) {
      let changed = true;
      while (changed) {
        changed = false;
        Array.from(next).forEach((h) => {
          const p = catalog.product(h);
          if (!p) { next.delete(h); changed = true; return; }
          const missing = p.requires.filter((r) => !stage.included.includes(r) && !next.has(r));
          if (missing.length) {
            next.delete(h);
            changed = true;
            notices.push({ type: 'info', text: t('autoRemoved', { removed: p.title, requires: list(missing.map((r) => (catalog.product(r) || {}).title || r)) }) });
          }
        });
      }
    },

    resolveToggle(ctx, current, handle, on) {
      const { catalog, platform, stage, t, list } = ctx;
      const next = new Set(current);
      const notices = [];
      const title = (h) => (catalog.product(h) || {}).title || h;

      if (on) {
        const status = Rules.optionStatus(catalog, platform, stage, handle, next);
        if (status.kind !== 'available') return { error: Rules.describe(ctx, status, handle), options: current, notices: [] };
        const added = [handle];
        next.add(handle);
        const queue = [handle];
        const guard = new Set([handle]);
        while (queue.length) {
          const h = queue.shift();
          const p = catalog.product(h);
          for (const r of (p ? p.requires : [])) {
            if (stage.included.includes(r) || next.has(r) || guard.has(r)) continue;
            guard.add(r);
            const rs = Rules.optionStatus(catalog, platform, stage, r, next);
            if (rs.kind !== 'available') {
              return { error: t('requiresUnavailable', { title: title(handle), items: title(r) }), options: current, notices: [] };
            }
            next.add(r);
            added.push(r);
            queue.push(r);
            notices.push({ type: 'info', text: t('autoAdded', { added: title(r), by: title(h) }) });
          }
        }
        const addedGroups = new Map();
        for (const a of added) {
          const g = (catalog.product(a) || {}).group;
          if (!g) continue;
          if (addedGroups.has(g)) return { error: t('conflictGroup', { items: list([title(addedGroups.get(g)), title(a)]) }), options: current, notices: [] };
          addedGroups.set(g, a);
        }
        addedGroups.forEach((a, g) => {
          Array.from(next).forEach((other) => {
            if (other === a || added.includes(other)) return;
            if ((catalog.product(other) || {}).group === g) {
              next.delete(other);
              notices.push({ type: 'info', text: t('replaced', { removed: title(other), added: title(a) }) });
            }
          });
        });
      } else {
        next.delete(handle);
      }
      Rules.pruneDependents(catalog, stage, next, notices, t, list);
      return { options: next, notices };
    },

    /** When the stage changes: keep what is still valid, add stage defaults, dedupe groups. */
    carryOver(ctx, prevOptions, userRemoved, applyDefaults, announceDrops) {
      const { catalog, platform, stage, t, list } = ctx;
      const next = new Set();
      const notices = [];
      const dropped = [];
      prevOptions.forEach((h) => {
        const s = Rules.optionStatus(catalog, platform, stage, h, next);
        if (s.kind === 'available') next.add(h);
        else if (s.kind !== 'included') dropped.push(h);
      });
      if (applyDefaults) {
        stage.defaults.forEach((h) => {
          if (userRemoved.has(h) || next.has(h)) return;
          if (Rules.optionStatus(catalog, platform, stage, h, next).kind === 'available') next.add(h);
        });
      }
      const groups = new Map();
      Array.from(next).forEach((h) => {
        const g = (catalog.product(h) || {}).group;
        if (!g) return;
        if (groups.has(g)) { next.delete(h); dropped.push(h); } else groups.set(g, h);
      });
      Rules.pruneDependents(catalog, stage, next, notices, t, list);
      if (announceDrops && dropped.length) {
        notices.unshift({ type: 'info', text: t('droppedOnStage', { items: list(dropped.map((h) => (catalog.product(h) || {}).title || h)), stage: stage.name }) });
      }
      return { options: next, notices };
    },

    describe(ctx, status, handle) {
      const { catalog, stage, t, list } = ctx;
      const product = catalog.product(handle) || { title: handle };
      switch (status.kind) {
        case 'no-stage': return t('chooseStage');
        case 'included': return t('includedWith', { stage: stage ? stage.name : '' });
        case 'other-stage':
          return status.stages && status.stages.length
            ? t('availableWith', { stages: list(status.stages.map((s) => s.name)) })
            : t('notOffered', { stage: stage ? stage.name : '' });
        case 'out-of-stock': return `${product.title}: ${t('outOfStock')}`;
        case 'blocked':
          if (status.reasonKey === 'requiresUnavailable') return t('requiresUnavailable', { title: product.title, items: list(status.items) });
          if (status.reasonKey === 'conflictsIncluded') return t('conflictsIncluded', { items: list(status.items) });
          return t('unavailable');
        default: return '';
      }
    },

    validate(ctx, state, customer) {
      const { catalog, t, list } = ctx;
      const errors = [];
      const warnings = [];
      const platform = catalog.platform(state.platformKey);
      if (!platform) {
        errors.push({ code: 'no-platform', text: t('choosePlatform') });
        return { ok: false, errors, warnings, stageStatus: null };
      }
      const stage = catalog.stage(state.stageId);
      if (!stage || !platform.stages.includes(stage.id)) {
        errors.push({ code: 'no-stage', text: t('chooseStage') });
        return { ok: false, errors, warnings, stageStatus: null };
      }
      const scoped = Object.assign({}, ctx, { platform, stage });
      const status = Rules.stageStatus(catalog, stage);
      if (status.state === 'invalid') errors.push({ code: 'stage-invalid', text: t('stageMisconfigured') });
      if (status.state === 'unavailable') {
        errors.push({ code: 'stage-unavailable', text: t('stageUnavailable', { stage: stage.name, items: list(status.blocking.map((p) => p.title)) }) });
      }
      const covered = new Set();
      stage.included.concat(Array.from(state.options)).forEach((h) => {
        const g = (catalog.product(h) || {}).group;
        if (g) covered.add(g);
      });
      stage.requiredGroups.forEach((g) => {
        if (!covered.has(g)) errors.push({ code: 'required-group', text: t('requiredGroup', { group: Util.humanize(g).toLowerCase() }) });
      });
      const unanswered = catalog.requirementsFor(stage)
        .filter((r) => Rules.requirementState(catalog, stage, r, state.options, state.owned).kind === 'unanswered')
        .map((r) => r.name);
      if (unanswered.length) errors.push({ code: 'requirement', text: t('requirementNeeded', { items: list(unanswered) }) });
      const seenGroups = new Map();
      state.options.forEach((h) => {
        const others = new Set(state.options);
        others.delete(h);
        const s = Rules.optionStatus(catalog, platform, stage, h, others);
        if (s.kind !== 'available') errors.push({ code: 'option-invalid', text: Rules.describe(scoped, s, h) });
        const g = (catalog.product(h) || {}).group;
        if (g) {
          if (seenGroups.has(g)) errors.push({ code: 'group-conflict', text: t('conflictGroup', { items: list([seenGroups.get(g), (catalog.product(h) || {}).title]) }) });
          else seenGroups.set(g, (catalog.product(h) || {}).title);
        }
      });
      const optionProducts = Array.from(state.options).map((h) => catalog.product(h)).filter(Boolean);
      const backorders = status.backorder.concat(optionProducts.filter((p) => p.backorder));
      if (stage.pricingMode === 'bundle_product') {
        const bundle = catalog.product(stage.bundle);
        if (bundle && bundle.backorder && !backorders.length) backorders.push(bundle);
      }
      if (backorders.length) warnings.push({ code: 'backorder', text: t('backorderWarning', { items: list(backorders.map((p) => p.title)) }) });

      if (customer && customer.dealer && customer.dealerMode === 'tag_metafield') {
        const lines = Rules.cartProducts(catalog, stage, state.options);
        const missing = lines.filter((p) => p.dealerMissing);
        if (missing.length) warnings.push({ code: 'dealer-missing', text: t('dealerMissing', { items: list(missing.map((p) => p.title)) }) });
      }
      return { ok: errors.length === 0, errors, warnings, stageStatus: status };
    },

    /** The exact merchandise that goes to the cart. */
    cartProducts(catalog, stage, options) {
      if (!stage) return [];
      const lines = [];
      if (stage.pricingMode === 'bundle_product') {
        const bundle = catalog.product(stage.bundle);
        if (bundle) lines.push(bundle);
      } else {
        stage.included.forEach((h) => { const p = catalog.product(h); if (p) lines.push(p); });
      }
      options.forEach((h) => { const p = catalog.product(h); if (p) lines.push(p); });
      return lines;
    }
  };

  /* ------------------------------------------------------------------ *
   * Pricing — display maths only. Shopify charges the same numbers because
   * every figure here is a Shopify-rendered variant price.
   * ------------------------------------------------------------------ */
  const Pricing = {
    calculate(catalog, stage, options) {
      if (!stage) return null;
      const lineTotal = (p) => p.price * p.qty;
      const lineMsrp = (p) => (p.msrp !== null ? p.msrp : p.price) * p.qty;
      const sum = (arr, fn) => arr.reduce((acc, p) => acc + fn(p), 0);
      const included = stage.included.map((h) => catalog.product(h)).filter(Boolean);
      const componentsTotal = sum(included, lineTotal);
      let mode = 'sum';
      let packagePrice = componentsTotal;
      let packageMsrp = sum(included, lineMsrp);
      let savings = 0;
      let bundle = null;
      if (stage.pricingMode === 'bundle_product') {
        bundle = catalog.product(stage.bundle);
        if (bundle && bundle.priceKnown) {
          mode = 'bundle';
          packagePrice = lineTotal(bundle);
          packageMsrp = lineMsrp(bundle);
          savings = Math.max(0, componentsTotal - packagePrice);
        } else {
          mode = 'invalid';
        }
      }
      const optionProducts = Array.from(options || []).map((h) => catalog.product(h)).filter(Boolean);
      const optionLines = optionProducts.map((p) => ({ handle: p.handle, title: p.title, price: lineTotal(p), msrp: lineMsrp(p) }));
      const optionsTotal = optionLines.reduce((a, l) => a + l.price, 0);
      const optionsMsrp = optionLines.reduce((a, l) => a + l.msrp, 0);
      const total = packagePrice + optionsTotal;
      const msrpTotal = packageMsrp + optionsMsrp;
      return {
        mode, componentsTotal, packagePrice, packageMsrp, savings, bundle,
        includedCount: included.length, optionLines, optionsTotal, total, msrpTotal
      };
    }
  };

  /* ------------------------------------------------------------------ *
   * Dyno — honest matching.
   *   A run qualifies only if every option it tested is in the current build
   *   (tested ⊆ selected). The largest qualifying run wins; ties use priority.
   *   A run that tested parts the customer did NOT select is never shown,
   *   because it would overstate their build.
   * ------------------------------------------------------------------ */
  const Dyno = {
    match(catalog, stage, options, owned) {
      if (!stage) return { kind: 'no-stage' };
      // Parts the customer already has count as fitted (they're on the bike), not as purchases.
      const fitted = new Set(options);
      if (owned) owned.forEach((o) => { if (o && o.type === 'product') fitted.add(o.handle); });
      const relevant = new Set(Array.from(fitted).filter((h) => {
        const p = catalog.product(h);
        return p && p.affectsDyno !== false && !stage.included.includes(h);
      }));
      let best = null;
      catalog.dynoRuns(stage.id).forEach((run) => {
        const tested = new Set(run.tested.filter((h) => !stage.included.includes(h)));
        for (const h of tested) if (!relevant.has(h)) return;
        const better = !best
          || tested.size > best.tested.size
          || (tested.size === best.tested.size && (run.priority || 0) > (best.run.priority || 0));
        if (better) best = { run, tested };
      });
      if (!best) return { kind: 'none', relevant: Array.from(relevant) };
      const notRepresented = Array.from(relevant).filter((h) => !best.tested.has(h));
      return {
        kind: notRepresented.length ? 'partial' : 'exact',
        run: best.run,
        tested: Array.from(best.tested),
        notRepresented
      };
    }
  };

  /* ------------------------------------------------------------------ *
   * Cart API — Shopify AJAX cart. Uses locale-aware routes from Liquid.
   * ------------------------------------------------------------------ */
  const CartApi = {
    create(routes) {
      const cartUrl = (routes.cart || '/cart').replace(/\/$/, '');
      const addUrl = (routes.cartAdd || '/cart/add').replace(/\/$/, '');
      const updateUrl = (routes.cartUpdate || '/cart/update').replace(/\/$/, '');
      const readJson = async (res) => {
        try { return await res.json(); } catch (e) { return null; }
      };
      const post = async (url, body) => {
        const res = await fetch(`${url}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const json = await readJson(res);
        if (!res.ok) {
          const msg = (json && (json.description || json.message)) || `Cart request failed (${res.status}).`;
          const err = new Error(typeof msg === 'string' ? msg : 'Cart request failed.');
          err.status = res.status;
          throw err;
        }
        return json || {};
      };
      return {
        cartUrl,
        add(items) { return post(addUrl, { items }); },
        async get() {
          const res = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(`Cart request failed (${res.status}).`);
          return res.json();
        },
        async removeBuild(buildId) {
          const cart = await this.get();
          const updates = {};
          (cart.items || []).forEach((item) => {
            if (item.properties && item.properties._af_build_id === buildId) updates[item.key] = 0;
          });
          if (!Object.keys(updates).length) return 0;
          await post(updateUrl, { updates });
          return Object.keys(updates).length;
        }
      };
    }
  };

  /* ------------------------------------------------------------------ *
   * Controller
   * ------------------------------------------------------------------ */
  class AirforceBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.state = { platformKey: null, stageId: null, options: new Set(), userRemoved: new Set(), owned: new Map(), busy: false };
      this.derived = {};
      this.pendingNotices = [];
      this.cartMessage = null;
      this.started = false;
      this.renderedStagesFor = null;
      this.renderedOptionsFor = null;
      this.renderedReqFor = null;
      this.stageNodes = new Map();
      this.optionNodes = new Map();
      this.reqNodes = new Map();
      this.renderedReqFor = null;
      this.observers = [];
      this.listeners = [];
      this.toastTimer = null;
      this.announceTimer = null;
      this.init();
    }

    /* ---------- lifecycle ---------- */
    init() {
      try {
        const script = Util.qs('[data-af-data]', this.root);
        if (!script) throw new Error('Builder data script missing.');
        const raw = JSON.parse(script.textContent);
        this.loadData(raw);
        this.strings = Object.assign({}, DEFAULT_STRINGS);
        Object.entries(raw.strings || {}).forEach(([k, v]) => { if (typeof v === 'string' && v.trim()) this.strings[k] = v; });
        this.t = (key, vars) => Util.template(this.strings[key] != null ? this.strings[key] : key, vars);
        this.locale = Util.safeLocale(raw.money && raw.money.locale);
        this.list = (items) => Util.list(items, this.locale);
        this.money = Money.create(raw.money || {});
        this.cart = CartApi.create(raw.routes || {});
        this.customer = raw.customer || {};
        this.settings = raw.settings || {};
        this.analytics = Analytics.create(this.root, {
          dealer: !!this.customer.dealer,
          currency: raw.money ? raw.money.currency : null
        });
        this.cacheDom();
        this.bindEvents();
        this.restoreConfigurationFromURL();
        this.root.dataset.state = 'ready';
        if (this.customer.dealer) {
          this.analytics.track('dealer_builder_used', { dealer_mode: this.customer.dealerMode }, 'dealer_builder_used');
        }
      } catch (error) {
        console.error('[AirforceBuilder] failed to initialise', error);
        this.showFatal();
      }
    }

    loadData(raw) {
      this.data = raw;
      this.catalog = createCatalog(raw);
    }

    destroy() {
      this.listeners.forEach(([target, type, fn]) => target.removeEventListener(type, fn));
      this.observers.forEach((o) => o.disconnect());
      this.listeners = [];
      this.observers = [];
      clearTimeout(this.toastTimer);
      clearTimeout(this.announceTimer);
    }

    on(target, type, fn) {
      if (!target) return;
      target.addEventListener(type, fn);
      this.listeners.push([target, type, fn]);
    }

    guard(fn) {
      try {
        return fn();
      } catch (error) {
        console.error('[AirforceBuilder]', error);
        this.flash(this.t('genericError'), 'error');
        return undefined;
      }
    }

    cacheDom() {
      const q = (s) => Util.qs(s, this.root);
      this.dom = {
        platformInputs: Util.qsa('[data-af-platform-input]', this.root),
        stageStep: q('[data-af-step="stage"]'),
        stageList: q('[data-af-stage-list]'),
        stageEmpty: q('[data-af-stage-empty]'),
        includedTitle: q('[data-af-included-title]'),
        includedList: q('[data-af-included-list]'),
        includedEmpty: q('[data-af-included-empty]'),
        stageDetail: q('[data-af-stage-detail]'),
        dyno: q('[data-af-dyno]'),
        perf: q('[data-af-perf]'),
        optionList: q('[data-af-option-list]'),
        optionsStep: q('[data-af-options-step]'),
        reqStep: q('[data-af-req-step]'),
        reqList: q('[data-af-req-list]'),
        optionsEmpty: q('[data-af-options-empty]'),
        compat: q('[data-af-compat]'),
        compatBody: q('[data-af-compat-body]'),
        summaries: Util.qsa('[data-af-summary]', this.root),
        layout: q('[data-af-layout]'),
        mobileBar: q('[data-af-mobilebar]'),
        mobileTotal: q('[data-af-mobile-total]'),
        mobileLabel: q('[data-af-mobile-label]'),
        openSheet: q('[data-af-open-sheet]'),
        sheet: q('[data-af-sheet]'),
        lightbox: q('[data-af-lightbox]'),
        lightboxImg: q('[data-af-lightbox-img]'),
        lightboxCaption: q('[data-af-lightbox-caption]'),
        live: q('[data-af-live]'),
        notices: q('[data-af-notices]'),
        fatal: q('[data-af-fatal]'),
        toast: q('[data-af-toast]'),
        tpl: {
          stage: q('template[data-af-tpl="stage"]'),
          product: q('template[data-af-tpl="product"]'),
          option: q('template[data-af-tpl="option"]')
        }
      };
    }

    bindEvents() {
      this.on(this.root, 'change', (e) => this.guard(() => {
        const target = e.target;
        if (target.matches('[data-af-platform-input]')) this.selectPlatform(target.value, { source: 'user' });
        else if (target.matches('[data-af-stage-input]')) this.selectStage(target.value, { source: 'user' });
        else if (target.matches('[data-af-option-input]')) this.toggleOption(target.value, target.checked, { source: 'user' });
        else if (target.matches('[data-af-req-input]')) this.setRequirement(target.dataset.req, target.value, { source: 'user' });
      }));

      this.on(this.root, 'click', (e) => {
        const hit = (sel) => e.target.closest(sel);
        if (hit('[data-af-add]')) { e.preventDefault(); this.addBuildToCart(); return; }
        this.guard(() => {
          if (hit('[data-af-share]')) { e.preventDefault(); this.shareBuild(); }
          else if (hit('[data-af-open-sheet]')) { e.preventDefault(); this.openSheet(); }
          else if (hit('[data-af-close-sheet]')) { e.preventDefault(); this.closeSheet(); }
          else if (hit('[data-af-dyno-zoom]')) { e.preventDefault(); this.openLightbox('graph'); }
          else if (hit('[data-af-dyno-compare]')) { e.preventDefault(); this.openLightbox('comparison'); }
          else if (hit('[data-af-close-lightbox]')) { e.preventDefault(); this.closeDialog(this.dom.lightbox); }
          else if (hit('[data-af-dismiss-notices]')) { e.preventDefault(); this.dom.notices.replaceChildren(); this.dom.notices.hidden = true; }
          else if (e.target === this.dom.sheet) this.closeSheet();
          else if (e.target === this.dom.lightbox) this.closeDialog(this.dom.lightbox);
        });
      });

      // Hero / external "Build your 490" buttons anywhere on the page.
      const first = Util.qs('[data-af-builder]') === this.root;
      if (first) {
        this.on(document, 'click', (e) => {
          const link = e.target.closest('[data-af-select-platform]');
          if (!link || this.root.contains(link)) return;
          const key = link.getAttribute('data-af-select-platform');
          if (!this.catalog.platform(key)) return;
          e.preventDefault();
          this.guard(() => {
            this.selectPlatform(key, { source: 'hero' });
            this.scrollIntoView(this.dom.stageStep || this.root);
          });
        });
      }

      if (this.dom.sheet) this.on(this.dom.sheet, 'close', () => this.syncMobileBar());

      if (window.matchMedia) {
        const mq = window.matchMedia(DESKTOP_QUERY);
        const handler = () => { if (mq.matches) this.closeSheet(); };
        if (mq.addEventListener) { mq.addEventListener('change', handler); this.listeners.push([mq, 'change', handler]); }
      }

      if ('IntersectionObserver' in window) {
        if (this.dom.layout && this.dom.mobileBar) {
          const io = new IntersectionObserver((entries) => {
            this.layoutVisible = entries.some((en) => en.isIntersecting);
            this.syncMobileBar();
          }, { threshold: 0 });
          io.observe(this.dom.layout);
          this.observers.push(io);
        }
        if (this.dom.dyno) {
          const dio = new IntersectionObserver((entries) => {
            if (!entries.some((en) => en.isIntersecting)) return;
            const d = this.derived.dyno;
            if (d && d.run) {
              this.analytics.track('dyno_viewed', { platform: this.state.platformKey, stage: this.derived.stage && this.derived.stage.key, dyno_run: d.run.id, match: d.kind }, `dyno:${d.run.id}`);
            }
          }, { threshold: 0.4 });
          dio.observe(this.dom.dyno);
          this.observers.push(dio);
        }
      } else {
        this.layoutVisible = true;
      }
    }

    /* ---------- state mutators (no rendering) ---------- */
    ctx() {
      return {
        catalog: this.catalog,
        platform: this.catalog.platform(this.state.platformKey),
        stage: this.catalog.stage(this.state.stageId),
        t: this.t,
        list: this.list
      };
    }

    applyPlatform(key, { useDefaultStage = true } = {}) {
      const platform = this.catalog.platform(key);
      if (!platform) return false;
      const changed = this.state.platformKey !== platform.key;
      this.state.platformKey = platform.key;
      if (changed) {
        this.state.stageId = null;
        this.state.options = new Set();
        this.state.userRemoved = new Set();
        this.state.owned = new Map();
      }
      if (useDefaultStage && !this.state.stageId && platform.defaultStage && platform.stages.includes(platform.defaultStage)) {
        this.applyStage(platform.defaultStage, { applyDefaults: true, announceDrops: false });
      }
      return true;
    }

    applyStage(stageId, { applyDefaults = true, announceDrops = true } = {}) {
      const platform = this.catalog.platform(this.state.platformKey);
      const stage = this.catalog.stage(stageId);
      if (!platform || !stage || !platform.stages.includes(stage.id)) return false;
      const ctx = Object.assign(this.ctx(), { platform, stage });
      const result = Rules.carryOver(ctx, this.state.options, this.state.userRemoved, applyDefaults, announceDrops && !!this.state.stageId);
      this.state.stageId = stage.id;
      this.state.options = result.options;
      this.pendingNotices.push(...result.notices);
      return true;
    }

    applyToggle(handle, on) {
      const ctx = this.ctx();
      if (!ctx.platform || !ctx.stage) {
        this.pendingNotices.push({ type: 'error', text: this.t('chooseStage') });
        return false;
      }
      const result = Rules.resolveToggle(ctx, this.state.options, handle, on);
      if (result.error) {
        this.pendingNotices.push({ type: 'error', text: result.error });
        return false;
      }
      this.state.options = result.options;
      if (on) this.state.userRemoved.delete(handle);
      else this.state.userRemoved.add(handle);
      this.pendingNotices.push(...result.notices);
      return true;
    }

    /* ---------- public API ---------- */
    selectPlatform(key, { source = 'user' } = {}) {
      const before = this.state.platformKey;
      if (!this.applyPlatform(key)) return false;
      this.cartMessage = null;
      this.markStarted(source);
      if (before !== this.state.platformKey) {
        const p = this.catalog.platform(key);
        this.analytics.track('platform_selected', { platform: p.key, platform_name: p.name, source });
      }
      this.commit();
      return true;
    }

    selectStage(stageId, { source = 'user' } = {}) {
      if (!this.applyStage(stageId)) return false;
      this.cartMessage = null;
      this.markStarted(source);
      const stage = this.catalog.stage(stageId);
      this.analytics.track('stage_selected', { platform: this.state.platformKey, stage: stage.key, stage_name: stage.name, source });
      this.commit();
      return true;
    }

    toggleOption(handle, on, { source = 'user' } = {}) {
      const ok = this.applyToggle(handle, on);
      this.cartMessage = null;
      if (ok) {
        const p = this.catalog.product(handle);
        this.analytics.track(on ? 'option_added' : 'option_removed', {
          platform: this.state.platformKey,
          stage: this.derived.stage && this.derived.stage.key,
          product_handle: handle,
          product_title: p && p.title,
          variant_id: p && p.variantId,
          source
        });
      }
      this.commit();
      return ok;
    }

    /** value: 'buy:<handle>' | 'own:product:<handle>' | 'own:other:<key>' */
    applyRequirement(reqId, value) {
      const ctx = this.ctx();
      const req = this.catalog.requirement(reqId);
      if (!ctx.stage || !req) return false;
      const parts = String(value || '').split(':');
      if (parts[0] === 'buy') {
        const handle = parts.slice(1).join(':');
        if (!req.products.includes(handle)) return false;
        if (!this.applyToggle(handle, true)) return false;
        this.state.owned.delete(req.id);
        return true;
      }
      if (parts[0] !== 'own' || !req.allowOwned) return false;
      const choices = Rules.requirementChoices(this.catalog, ctx.stage, req).own;
      const choice = choices.find((c) => c.value === value);
      if (!choice) return false;
      req.products.forEach((h) => { if (this.state.options.has(h)) this.applyToggle(h, false); });
      this.state.owned.set(req.id, { type: choice.type, handle: choice.handle, key: choice.key, label: choice.label });
      return true;
    }

    setRequirement(reqId, value, { source = 'user' } = {}) {
      const req = this.catalog.requirement(reqId);
      const ok = this.applyRequirement(reqId, value);
      this.cartMessage = null;
      if (ok && req) {
        if (String(value).startsWith('buy:')) {
          const p = this.catalog.product(String(value).slice(4));
          this.analytics.track('option_added', { platform: this.state.platformKey, stage: this.derived.stage && this.derived.stage.key, product_handle: p && p.handle, product_title: p && p.title, variant_id: p && p.variantId, requirement: req.key, source });
        } else {
          const own = this.state.owned.get(req.id);
          this.analytics.track('requirement_owned', { platform: this.state.platformKey, stage: this.derived.stage && this.derived.stage.key, requirement: req.key, owned: own && own.key, source });
        }
      }
      this.commit();
      return ok;
    }

    validateConfiguration() {
      return Rules.validate(this.ctx(), this.state, this.customer);
    }

    calculatePrice() {
      return Pricing.calculate(this.catalog, this.catalog.stage(this.state.stageId), this.state.options);
    }

    matchDynoRun() {
      return Dyno.match(this.catalog, this.catalog.stage(this.state.stageId), this.state.options, this.state.owned);
    }

    commit({ updateUrl = true, announce = true } = {}) {
      const platform = this.catalog.platform(this.state.platformKey);
      const stage = this.catalog.stage(this.state.stageId);
      this.derived = {
        platform,
        stage,
        validation: this.validateConfiguration(),
        pricing: this.calculatePrice(),
        dyno: this.matchDynoRun()
      };
      this.render();
      if (updateUrl) this.updateURL();
      const notices = this.pendingNotices.splice(0);
      if (notices.length) {
        const worst = notices.find((n) => n.type === 'error') || notices[notices.length - 1];
        this.flash(notices.map((n) => n.text).join(' '), worst.type);
      } else if (announce && platform && stage && this.derived.pricing) {
        this.announceTotal();
      }
    }

    render() {
      this.root.dataset.platform = this.state.platformKey || '';
      this.root.toggleAttribute('data-has-stage', !!this.derived.stage);
      [
        () => this.renderPlatforms(),
        () => this.renderStages(),
        () => this.renderIncluded(),
        () => this.renderRequirements(),
        () => this.updateDynoDisplay(),
        () => this.renderOptions(),
        () => this.renderCompatibility(),
        () => this.updateSummary()
      ].forEach((fn) => {
        try { fn(); } catch (error) { console.error('[AirforceBuilder] render error', error); }
      });
    }

    /* ---------- platform + stage rendering ---------- */
    renderPlatforms() {
      const key = String(this.state.platformKey || '').toLowerCase();
      this.dom.platformInputs.forEach((input) => {
        const on = !!key && input.value.toLowerCase() === key;
        input.checked = on;
        const card = input.closest('[data-af-platform-card]');
        if (card) card.toggleAttribute('data-selected', on);
      });
    }

    renderStages() {
      const { platform, stage } = this.derived;
      const list = this.dom.stageList;
      const empty = this.dom.stageEmpty;
      if (!list) return;
      if (!platform) {
        list.replaceChildren();
        list.hidden = true;
        this.renderedStagesFor = null;
        if (empty) { empty.hidden = false; empty.textContent = this.t('choosePlatform'); }
        return;
      }
      const stages = this.catalog.stagesFor(platform);
      if (!stages.length) {
        list.replaceChildren();
        list.hidden = true;
        this.renderedStagesFor = null;
        if (empty) { empty.hidden = false; empty.textContent = this.t('noStages'); }
        return;
      }
      if (empty) empty.hidden = true;
      list.hidden = false;
      if (this.renderedStagesFor !== platform.key) {
        const frag = document.createDocumentFragment();
        this.stageNodes = new Map();
        stages.forEach((s) => {
          const node = this.buildStageCard(s);
          if (node) { this.stageNodes.set(s.id, node); frag.appendChild(node); }
        });
        list.replaceChildren(frag);
        this.renderedStagesFor = platform.key;
      }
      stages.forEach((s) => this.updateStageCard(this.stageNodes.get(s.id), s, stage && stage.id === s.id));
    }

    buildStageCard(stage) {
      const tpl = this.dom.tpl.stage;
      if (!tpl) return null;
      const node = tpl.content.firstElementChild.cloneNode(true);
      const base = `af-stage-${this.sectionId}-${stage.id}`;
      const input = Util.qs('[data-af-stage-input]', node);
      input.name = `af-stage-${this.sectionId}`;
      input.value = stage.id;
      input.id = base;
      const name = Util.slot(node, 'name');
      name.id = `${base}-name`;
      name.textContent = stage.name;
      const desc = Util.slot(node, 'short');
      desc.id = `${base}-desc`;
      desc.textContent = stage.short || '';
      const price = Util.slot(node, 'price');
      price.id = `${base}-price`;
      const status = Util.slot(node, 'status');
      status.id = `${base}-status`;
      input.setAttribute('aria-labelledby', name.id);
      input.setAttribute('aria-describedby', `${price.id} ${status.id} ${desc.id}`);
      Util.slot(node, 'subtitle').textContent = stage.subtitle || '';
      const badge = Util.slot(node, 'badge');
      if (stage.badge) { badge.textContent = stage.badge; badge.hidden = false; }
      this.setImage(Util.slot(node, 'image'), stage.image, IMG_WIDTHS_WIDE.slice(0, 3), '(min-width: 750px) 30vw, 1px');
      node.dataset.stage = stage.id;
      return node;
    }

    updateStageCard(node, stage, isSelected) {
      if (!node) return;
      const input = Util.qs('[data-af-stage-input]', node);
      input.checked = isSelected;
      node.toggleAttribute('data-selected', isSelected);
      const pricing = Pricing.calculate(this.catalog, stage, new Set());
      const status = Rules.stageStatus(this.catalog, stage);
      node.dataset.status = status.state;
      const price = Util.slot(node, 'price');
      price.textContent = pricing && pricing.mode !== 'invalid' ? this.money.format(pricing.packagePrice) : this.t('priceUnavailable');
      const savings = Util.slot(node, 'savings');
      if (pricing && pricing.mode === 'bundle' && pricing.savings > 0) {
        savings.hidden = false;
        savings.textContent = `${this.t('packageSavings')} ${this.money.format(pricing.savings)}`;
      } else {
        savings.hidden = true;
      }
      const msrp = Util.slot(node, 'msrp');
      if (msrp) {
        const show = this.customer.dealer && this.customer.showMsrp && pricing && pricing.packageMsrp > pricing.packagePrice;
        msrp.hidden = !show;
        if (show) msrp.textContent = `${this.t('msrp')} ${this.money.format(pricing.packageMsrp)}`;
      }
      const statusEl = Util.slot(node, 'status');
      if (status.state === 'unavailable' || status.state === 'invalid') {
        statusEl.hidden = false;
        statusEl.textContent = this.t('temporarilyUnavailable');
      } else if (status.state === 'backorder') {
        statusEl.hidden = false;
        statusEl.textContent = this.t('backorderLong');
      } else {
        statusEl.hidden = true;
        statusEl.textContent = '';
      }
      const fit = Util.slot(node, 'fitment');
      if (fit) {
        const text = this.fitText(Rules.stageFitment(this.catalog, stage));
        fit.textContent = text;
        fit.hidden = !text;
      }
      Util.slot(node, 'cta').textContent = isSelected ? `✓ ${this.t('selected')}` : this.t('select');
    }

    /* ---------- included components ---------- */
    renderIncluded() {
      const { stage, pricing } = this.derived;
      const list = this.dom.includedList;
      if (!list) return;
      if (!stage) {
        list.replaceChildren();
        list.hidden = true;
        if (this.dom.includedEmpty) this.dom.includedEmpty.hidden = false;
        if (this.dom.includedTitle) this.dom.includedTitle.hidden = true;
        if (this.dom.stageDetail) this.dom.stageDetail.replaceChildren();
        return;
      }
      if (this.dom.includedEmpty) this.dom.includedEmpty.hidden = true;
      if (this.dom.includedTitle) {
        this.dom.includedTitle.hidden = false;
        this.dom.includedTitle.textContent = this.t('includedTitle', { stage: stage.name });
      }
      list.hidden = false;
      const frag = document.createDocumentFragment();
      stage.included.forEach((h) => {
        const p = this.catalog.product(h);
        if (!p || !this.dom.tpl.product) return;
        const node = this.dom.tpl.product.content.firstElementChild.cloneNode(true);
        Util.slot(node, 'includedBadge').textContent = this.t('included');
        const rec = Util.slot(node, 'recommended');
        if (stage.recommended.includes(h)) { rec.hidden = false; rec.textContent = this.t('recommended'); }
        Util.slot(node, 'title').textContent = p.title;
        const link = Util.slot(node, 'link');
        if (p.url) link.href = p.url; else link.removeAttribute('href');
        Util.slot(node, 'short').textContent = p.short || '';
        const priceEl = Util.slot(node, 'price');
        if (this.settings.showComponentPrices && p.priceKnown) {
          const formatted = this.money.format(p.price * p.qty);
          priceEl.textContent = pricing && pricing.mode === 'bundle' ? this.t('value', { price: formatted }) : formatted;
        } else {
          priceEl.textContent = '';
        }
        const statusEl = Util.slot(node, 'status');
        if (!p.available || p.builderEnabled === false) { statusEl.hidden = false; statusEl.textContent = this.t('outOfStock'); node.dataset.status = 'unavailable'; }
        else if (p.backorder) { statusEl.hidden = false; statusEl.textContent = this.t('backorderLong'); node.dataset.status = 'backorder'; }
        const fitEl = Util.slot(node, 'fitment');
        if (fitEl) {
          const fitTxt = this.fitText({ from: p.fitFrom, to: p.fitTo });
          fitEl.textContent = fitTxt;
          fitEl.hidden = !fitTxt;
        }
        this.setImage(Util.slot(node, 'image'), p.image, IMG_WIDTHS_CARD, '96px');
        frag.appendChild(node);
      });
      list.replaceChildren(frag);
      this.renderStageDetail(stage);
    }

    renderStageDetail(stage) {
      const box = this.dom.stageDetail;
      if (!box) return;
      box.replaceChildren();
      if (stage.longHtml) {
        const rte = Util.el('div', 'af-rte');
        rte.innerHTML = stage.longHtml; // Admin-authored rich text rendered by Shopify's metafield_tag.
        box.appendChild(rte);
      }
      const facts = [];
      if (stage.intendedUse) facts.push(['intendedUse', stage.intendedUseLabel || '', stage.intendedUse]);
      if (stage.perfNotes) facts.push(['perfNotes', this.t('performanceNotes'), stage.perfNotes]);
      if (facts.length) {
        const dl = Util.el('dl', 'af-facts');
        facts.forEach(([, label, value]) => {
          if (label) dl.appendChild(Util.el('dt', null, label));
          dl.appendChild(Util.el('dd', null, value));
        });
        box.appendChild(dl);
      }
    }

    /* ---------- dyno ---------- */
    describeConfiguration(platform, stage, testedHandles) {
      const parts = [`${platform ? `${platform.name} ` : ''}${stage.name}`];
      (testedHandles || []).forEach((h) => { const p = this.catalog.product(h); if (p) parts.push(p.title); });
      return parts.join(' + ');
    }

    updateDynoDisplay() {
      const el = this.dom.dyno;
      const { platform, stage, dyno } = this.derived;
      const perf = this.dom.perf;
      if (!el) return;
      const s = (name) => Util.slot(el, name);
      const setText = (name, value) => { const n = s(name); if (n) n.textContent = value; };
      const hpText = (v) => (v === null ? '—' : `${Util.formatNumber(v, this.locale, 1)} ${this.t('hp')}`);
      const signed = (v) => (v > 0 ? `+${Util.formatNumber(v, this.locale, 1)}` : Util.formatNumber(v, this.locale, 1));
      const graphBtn = s('graphBtn');
      const graph = s('graph');
      const placeholder = s('placeholder');
      const compareBtn = s('compareBtn');
      const details = s('details');

      const reset = (message) => {
        el.dataset.match = dyno ? dyno.kind : 'no-stage';
        ['stockHp', 'stockTq', 'buildHp', 'buildTq', 'hpGain', 'tqGain'].forEach((n) => setText(n, '—'));
        setText('buildLabel', this.t('yourBuild'));
        setText('match', message);
        if (graphBtn) graphBtn.hidden = true;
        if (placeholder) { placeholder.hidden = false; placeholder.textContent = message; }
        if (compareBtn) compareBtn.hidden = true;
        if (details) { details.replaceChildren(); details.closest('details').hidden = true; }
        const notes = s('notes'); if (notes) notes.replaceChildren();
        const disc = s('disclaimer'); if (disc) disc.replaceChildren();
        this.renderPerf(null);
      };

      if (!stage) { reset(this.t('dynoNoStage')); return; }
      if (!dyno || dyno.kind === 'none') { reset(this.t('dynoNone')); this.renderPlatformDisclaimer(platform); return; }

      const run = dyno.run;
      el.dataset.match = dyno.kind;
      const testedLabel = this.describeConfiguration(platform, stage, dyno.tested);
      const missingLabel = this.list(dyno.notRepresented.map((h) => (this.catalog.product(h) || {}).title || h));

      if (run.graph && run.graph.src) {
        graphBtn.hidden = false;
        placeholder.hidden = true;
        graphBtn.setAttribute('aria-label', `${this.t('enlargeGraph')}: ${testedLabel}`);
        if (graph.dataset.src !== run.graph.src) {
          graphBtn.setAttribute('data-loading', '');
          graph.onload = () => graphBtn.removeAttribute('data-loading');
          graph.onerror = () => { graphBtn.removeAttribute('data-loading'); graphBtn.hidden = true; placeholder.hidden = false; placeholder.textContent = this.t('dynoGraphMissing'); };
          graph.dataset.src = run.graph.src;
          this.setImage(graph, Object.assign({}, run.graph, { alt: run.graph.alt || testedLabel }), IMG_WIDTHS_WIDE, '(min-width: 990px) 60vw, 100vw');
        }
      } else {
        graphBtn.hidden = true;
        placeholder.hidden = false;
        placeholder.textContent = this.t('dynoGraphMissing');
      }
      compareBtn.hidden = !(run.comparison && run.comparison.src);

      const unit = run.tqUnit || 'lb-ft';
      const tqText = (v) => (v === null ? '—' : `${Util.formatNumber(v, this.locale, 1)} ${unit}`);
      setText('stockHp', hpText(run.stockHp));
      setText('stockTq', tqText(run.stockTq));
      setText('buildHp', hpText(run.hp));
      setText('buildTq', tqText(run.tq));
      setText('buildLabel', dyno.kind === 'exact' ? this.t('yourBuild') : this.t('testedConfig'));
      const hpGain = run.hp !== null && run.stockHp !== null ? run.hp - run.stockHp : null;
      const tqGain = run.tq !== null && run.stockTq !== null ? run.tq - run.stockTq : null;
      setText('hpGain', hpGain === null ? '—' : `${signed(hpGain)} ${this.t('hp')}`);
      setText('tqGain', tqGain === null ? '—' : `${signed(tqGain)} ${unit}`);
      setText('match', dyno.kind === 'exact'
        ? this.t('dynoExact', { tested: testedLabel })
        : this.t('dynoPartial', { tested: testedLabel, missing: missingLabel }));

      if (details) {
        const rows = [
          [this.t('testConfiguration'), testedLabel],
          [this.t('peakHpAt'), run.hpRpm !== null ? `${Util.formatNumber(run.hpRpm, this.locale, 0)} ${this.t('rpm')}` : ''],
          [this.t('peakTqAt'), run.tqRpm !== null ? `${Util.formatNumber(run.tqRpm, this.locale, 0)} ${this.t('rpm')}` : ''],
          [this.t('testBike'), run.bike],
          [this.t('fuel'), run.fuel],
          [this.t('dynoType'), run.dynoType],
          [this.t('testDate'), run.date],
          [this.t('conditions'), run.conditions]
        ].filter((r) => r[1]);
        details.replaceChildren();
        rows.forEach(([k, v]) => {
          const row = Util.el('div', 'af-dl-row');
          row.appendChild(Util.el('dt', null, k));
          row.appendChild(Util.el('dd', null, v));
          details.appendChild(row);
        });
        details.closest('details').hidden = rows.length === 0;
      }
      const notes = s('notes');
      if (notes) {
        notes.replaceChildren();
        if (run.notesHtml) { const d = Util.el('div', 'af-rte'); d.innerHTML = run.notesHtml; notes.appendChild(d); }
      }
      const disc = s('disclaimer');
      if (disc) {
        disc.replaceChildren();
        const html = run.disclaimerHtml || (platform && platform.disclaimerHtml);
        if (html) { const d = Util.el('div', 'af-rte'); d.innerHTML = html; disc.appendChild(d); }
      }
      this.renderPerf({ run, dyno, hpGain, tqGain, unit, testedLabel });
      if (perf) perf.hidden = false;
    }

    renderPlatformDisclaimer(platform) {
      const disc = Util.slot(this.dom.dyno, 'disclaimer');
      if (!disc || !platform || !platform.disclaimerHtml) return;
      const d = Util.el('div', 'af-rte');
      d.innerHTML = platform.disclaimerHtml;
      disc.replaceChildren(d);
    }

    renderPerf(info) {
      const perf = this.dom.perf;
      if (!perf) return;
      const card = (key) => Util.qs(`[data-perf="${key}"]`, perf);
      const set = (key, value, label) => {
        const c = card(key);
        if (!c) return;
        Util.slot(c, 'value').textContent = value;
        Util.slot(c, 'label').textContent = label;
        c.toggleAttribute('data-empty', value === '—');
      };
      const stage = this.derived.stage;
      const caption = Util.slot(perf, 'caption');
      if (!info) {
        set('hp', '—', this.t('peakGain'));
        set('tq', '—', this.t('torqueGain'));
        set('rpm', '—', this.t('peakPowerRpm'));
        set('config', stage ? stage.name : '—', this.t('configuration'));
        if (caption) caption.textContent = stage ? this.t('notTested') : '';
        return;
      }
      const { run, dyno, hpGain, tqGain, unit, testedLabel } = info;
      const signed = (v) => (v > 0 ? `+${Util.formatNumber(v, this.locale, 1)}` : Util.formatNumber(v, this.locale, 1));
      set('hp', hpGain === null ? '—' : `${signed(hpGain)} ${this.t('hp')}`, this.t('peakGain'));
      set('tq', tqGain === null ? '—' : `${signed(tqGain)} ${unit}`, this.t('torqueGain'));
      set('rpm', run.hpRpm === null ? '—' : `${Util.formatNumber(run.hpRpm, this.locale, 0)} ${this.t('rpm')}`, this.t('peakPowerRpm'));
      set('config', stage.name, this.t('configuration'));
      if (caption) caption.textContent = dyno.kind === 'exact' ? '' : this.t('basedOn', { tested: testedLabel });
    }

    /* ---------- options ---------- */
    renderOptions() {
      const { platform, stage } = this.derived;
      const list = this.dom.optionList;
      const empty = this.dom.optionsEmpty;
      if (!list) return;
      if (!platform) {
        list.replaceChildren();
        list.hidden = true;
        this.renderedOptionsFor = null;
        if (this.dom.optionsStep) this.dom.optionsStep.hidden = false;
        if (empty) { empty.hidden = false; empty.textContent = this.t('choosePlatform'); }
        return;
      }
      const handles = Rules.optionHandles(this.catalog, platform);
      if (!handles.length) {
        list.replaceChildren();
        list.hidden = true;
        this.renderedOptionsFor = null;
        if (empty) { empty.hidden = false; empty.textContent = this.t('noOptions'); }
        // Everything optional is handled under "Complete your package" — hide the empty step.
        if (this.dom.optionsStep) this.dom.optionsStep.hidden = this.catalog.requirementProductsFor(platform).size > 0;
        return;
      }
      if (this.dom.optionsStep) this.dom.optionsStep.hidden = false;
      if (empty) empty.hidden = true;
      list.hidden = false;
      if (this.renderedOptionsFor !== platform.key) {
        const frag = document.createDocumentFragment();
        this.optionNodes = new Map();
        handles.forEach((h) => {
          const node = this.buildOptionCard(this.catalog.product(h));
          if (node) { this.optionNodes.set(h, node); frag.appendChild(node); }
        });
        list.replaceChildren(frag);
        this.renderedOptionsFor = platform.key;
      }
      handles.forEach((h) => {
        const status = Rules.optionStatus(this.catalog, platform, stage, h, this.state.options);
        this.updateOptionCard(this.optionNodes.get(h), this.catalog.product(h), status, stage);
      });
    }

    buildOptionCard(product) {
      const tpl = this.dom.tpl.option;
      if (!tpl || !product) return null;
      const node = tpl.content.firstElementChild.cloneNode(true);
      const id = `af-opt-${this.sectionId}-${product.handle}`;
      node.dataset.handle = product.handle;
      const input = Util.qs('[data-af-option-input]', node);
      input.value = product.handle;
      input.id = id;
      const msg = Util.slot(node, 'message');
      msg.id = `${id}-msg`;
      const price = Util.slot(node, 'price');
      price.id = `${id}-price`;
      input.setAttribute('aria-describedby', `${price.id} ${msg.id}`);
      Util.slot(node, 'title').textContent = product.title;
      const link = Util.slot(node, 'link');
      if (product.url) link.href = product.url; else link.removeAttribute('href');
      Util.slot(node, 'short').textContent = product.short || '';
      this.setImage(Util.slot(node, 'image'), product.image, IMG_WIDTHS_CARD, '(min-width: 750px) 240px, 112px');
      const more = Util.slot(node, 'more');
      const body = Util.slot(node, 'moreBody');
      const blocks = [];
      if (product.longHtml) { const d = Util.el('div', 'af-rte'); d.innerHTML = product.longHtml; blocks.push(d); }
      if (product.perfNotes) { blocks.push(Util.el('h4', 'af-option__more-h', this.t('performanceNotes'))); blocks.push(Util.el('p', null, product.perfNotes)); }
      if (product.installNotes) { blocks.push(Util.el('h4', 'af-option__more-h', this.t('installationNotes'))); blocks.push(Util.el('p', null, product.installNotes)); }
      if (blocks.length && more && body) {
        more.hidden = false;
        Util.slot(node, 'moreLabel').textContent = this.t('details');
        blocks.forEach((b) => body.appendChild(b));
      }
      return node;
    }

    updateOptionCard(node, product, status, stage) {
      if (!node || !product) return;
      const input = Util.qs('[data-af-option-input]', node);
      const kind = status.kind;
      const selected = kind === 'available' && !!status.selected;
      node.dataset.state = kind;
      node.toggleAttribute('data-selected', selected || kind === 'included');
      input.checked = selected || kind === 'included';
      input.disabled = kind !== 'available';
      const formatted = product.priceKnown ? this.money.format(product.price * product.qty) : this.t('priceUnavailable');
      input.setAttribute('aria-label', `${this.t('addAria', { title: product.title })}, ${formatted}`);

      const action = Util.slot(node, 'action');
      const labels = {
        included: `✓ ${this.t('included')}`,
        'no-stage': this.t('selectStageFirst'),
        'out-of-stock': this.t('outOfStock'),
        'other-stage': this.t('unavailable'),
        blocked: this.t('unavailable')
      };
      action.textContent = kind === 'available' ? (selected ? `✓ ${this.t('added')}` : `${this.t('add')} ${product.title}`) : labels[kind] || '';

      const price = Util.slot(node, 'price');
      price.textContent = kind === 'included' ? this.t('includedWith', { stage: stage ? stage.name : '' }) : `+${formatted}`;
      const msrp = Util.slot(node, 'msrp');
      const showMsrp = kind !== 'included' && this.customer.dealer && this.customer.showMsrp && product.msrp !== null && product.msrp > product.price;
      msrp.hidden = !showMsrp;
      msrp.textContent = showMsrp ? `${this.t('msrp')} ${this.money.format(product.msrp * product.qty)}` : '';

      const message = Util.slot(node, 'message');
      let text = '';
      if (kind === 'other-stage' || kind === 'blocked') {
        const ctx = Object.assign(this.ctx(), { stage });
        text = Rules.describe(ctx, status, product.handle);
      } else if (kind === 'out-of-stock') text = this.t('outOfStock');
      else if (kind === 'available' && status.backorder) text = this.t('backorderLong');
      else if (kind === 'no-stage') text = this.t('chooseStage');
      message.textContent = text;
      message.hidden = !text;

      const rec = Util.slot(node, 'recommended');
      const isRec = !!(stage && stage.recommended.includes(product.handle));
      rec.hidden = !isRec;
      rec.textContent = isRec ? this.t('recommended') : '';
      const inc = Util.slot(node, 'included');
      inc.hidden = kind !== 'included';
      inc.textContent = this.t('included');
      const stock = Util.slot(node, 'stock');
      const stockText = kind === 'out-of-stock' ? this.t('outOfStock') : (kind === 'available' && status.backorder ? this.t('backorder') : '');
      stock.hidden = !stockText;
      stock.textContent = stockText;
      stock.dataset.tone = kind === 'out-of-stock' ? 'danger' : 'warn';
    }

    fitText(fit) {
      if (!fit) return '';
      const { from, to } = fit;
      if (from !== null && to !== null) return this.t('fitsRange', { from, to });
      if (from !== null) return this.t('fitsFrom', { from });
      if (to !== null) return this.t('fitsTo', { to });
      return '';
    }

    /* ---------- build requirements (e.g. ECU, pipe) ---------- */
    renderRequirements() {
      const step = this.dom.reqStep;
      const list = this.dom.reqList;
      if (!step || !list) return;
      const { stage } = this.derived;
      const reqs = this.catalog.requirementsFor(stage);
      if (!stage || !reqs.length) {
        step.hidden = true;
        list.replaceChildren();
        this.renderedReqFor = null;
        return;
      }
      step.hidden = false;
      if (this.renderedReqFor !== stage.id) {
        const frag = document.createDocumentFragment();
        this.reqNodes = new Map();
        reqs.forEach((r) => {
          const node = this.buildRequirement(stage, r);
          this.reqNodes.set(r.id, node);
          frag.appendChild(node);
        });
        list.replaceChildren(frag);
        this.renderedReqFor = stage.id;
      }
      reqs.forEach((r) => this.updateRequirement(this.reqNodes.get(r.id), stage, r));
    }

    buildRequirement(stage, req) {
      const base = `af-req-${this.sectionId}-${req.id}`;
      const fs = Util.el('fieldset', 'af-req');
      fs.dataset.req = req.id;
      const legend = Util.el('legend', 'af-req__legend', req.name);
      fs.appendChild(legend);
      if (req.description) fs.appendChild(Util.el('p', 'af-req__desc', req.description));
      const inc = Util.el('p', 'af-req__included');
      inc.hidden = true;
      fs.appendChild(inc);
      const choices = Util.el('div', 'af-req__choices');
      const { buy, own } = Rules.requirementChoices(this.catalog, stage, req);
      const radio = (value) => {
        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'af-vh';
        input.name = base;
        input.value = value;
        input.dataset.req = req.id;
        input.setAttribute('data-af-req-input', '');
        return input;
      };
      buy.forEach((h) => {
        const p = this.catalog.product(h);
        const wrap = Util.el('div', 'af-choice-wrap');
        const label = Util.el('label', 'af-choice af-choice--buy');
        label.dataset.value = `buy:${h}`;
        const input = radio(`buy:${h}`);
        const media = Util.el('span', 'af-choice__media is-empty');
        media.setAttribute('data-media', '');
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.width = 96;
        img.height = 96;
        img.hidden = true;
        media.appendChild(img);
        const text = Util.el('span', 'af-choice__text');
        const top = Util.el('span', 'af-choice__top');
        top.appendChild(Util.el('span', 'af-choice__title', `${this.t('add')} ${p.title}`));
        const rec = Util.el('span', 'af-badge af-badge--accent', this.t('recommended'));
        rec.dataset.role = 'rec';
        rec.hidden = !stage.recommended.includes(h);
        top.appendChild(rec);
        const stock = Util.el('span', 'af-badge af-badge--stock');
        stock.dataset.role = 'stock';
        stock.hidden = true;
        top.appendChild(stock);
        text.appendChild(top);
        if (p.short) text.appendChild(Util.el('span', 'af-choice__short', p.short));
        const fitTxt = this.fitText({ from: p.fitFrom, to: p.fitTo });
        if (fitTxt) text.appendChild(Util.el('span', 'af-choice__fit', fitTxt));
        const price = Util.el('span', 'af-choice__price');
        price.dataset.role = 'price';
        label.append(input, media, text, price);
        wrap.appendChild(label);
        if (p.url) {
          const a = Util.el('a', 'af-choice__link', this.t('viewProduct'));
          a.href = p.url;
          a.setAttribute('aria-label', `${this.t('viewProduct')}: ${p.title}`);
          wrap.appendChild(a);
        }
        choices.appendChild(wrap);
        this.setImage(img, p.image, IMG_WIDTHS_CARD, '72px');
      });
      if (own.length) {
        const ownBox = Util.el('div', 'af-req__own');
        ownBox.appendChild(Util.el('p', 'af-req__own-label', this.t('alreadyHave')));
        const ownList = Util.el('div', 'af-req__own-list');
        own.forEach((c) => {
          const label = Util.el('label', 'af-choice af-choice--own');
          label.dataset.value = c.value;
          const input = radio(c.value);
          input.setAttribute('aria-label', this.t('alreadyHaveAria', { item: c.label }));
          label.append(input, Util.el('span', 'af-choice__title', c.label));
          ownList.appendChild(label);
        });
        ownBox.appendChild(ownList);
        choices.appendChild(ownBox);
      }
      fs.appendChild(choices);
      return fs;
    }

    updateRequirement(node, stage, req) {
      if (!node) return;
      const state = Rules.requirementState(this.catalog, stage, req, this.state.options, this.state.owned);
      node.dataset.state = state.kind;
      const inc = Util.qs('.af-req__included', node);
      const choices = Util.qs('.af-req__choices', node);
      if (state.kind === 'included') {
        const p = this.catalog.product(state.handle);
        inc.textContent = `✓ ${this.t('requirementIncluded', { title: p ? p.title : '', stage: stage.name })}`;
        inc.hidden = false;
        choices.hidden = true;
        return;
      }
      inc.hidden = true;
      choices.hidden = false;
      const current = state.kind === 'buy' ? `buy:${state.handle}`
        : state.kind === 'own' ? (state.own.type === 'product' ? `own:product:${state.own.handle}` : `own:other:${state.own.key}`)
          : '';
      Util.qsa('[data-af-req-input]', node).forEach((input) => {
        const on = input.value === current;
        input.checked = on;
        const label = input.closest('.af-choice');
        if (label) label.toggleAttribute('data-selected', on);
        if (!input.value.startsWith('buy:')) return;
        const h = input.value.slice(4);
        const p = this.catalog.product(h);
        const others = new Set(this.state.options);
        others.delete(h);
        const status = Rules.optionStatus(this.catalog, this.derived.platform, stage, h, others);
        const buyable = status.kind === 'available';
        input.disabled = !buyable && !on;
        if (label) label.toggleAttribute('data-disabled', input.disabled);
        const price = Util.qs('[data-role="price"]', label);
        const formatted = p && p.priceKnown ? this.money.format(p.price * p.qty) : this.t('priceUnavailable');
        price.textContent = `+${formatted}`;
        input.setAttribute('aria-label', `${this.t('addAria', { title: p ? p.title : h })}, ${formatted}`);
        const stock = Util.qs('[data-role="stock"]', label);
        const stockText = status.kind === 'out-of-stock' ? this.t('outOfStock') : (p && p.backorder ? this.t('backorder') : '');
        stock.textContent = stockText;
        stock.hidden = !stockText;
        stock.dataset.tone = status.kind === 'out-of-stock' ? 'danger' : 'warn';
      });
    }

    renderCompatibility() {
      const { platform } = this.derived;
      const box = this.dom.compat;
      if (!box || !this.dom.compatBody) return;
      if (!platform || !platform.compatHtml) {
        box.hidden = true;
        this.dom.compatBody.replaceChildren();
        return;
      }
      box.hidden = false;
      const d = Util.el('div', 'af-rte');
      d.innerHTML = platform.compatHtml;
      this.dom.compatBody.replaceChildren(d);
    }

    /* ---------- summary ---------- */
    updateSummary() {
      const { platform, stage, pricing, validation } = this.derived;
      const machine = platform ? `${platform.name}${stage ? ` — ${stage.name}` : ''}` : this.t('choosePlatform');
      const totalText = pricing && pricing.mode !== 'invalid' ? this.money.formatWithCurrency(pricing.total) : '—';

      this.dom.summaries.forEach((root) => {
        const s = (n) => Util.slot(root, n);
        s('machine').textContent = machine;

        const dealer = s('dealer');
        if (dealer) {
          let text = '';
          if (this.customer.dealer) {
            text = this.t('dealerPricing') + (this.customer.dealerCompany ? ` — ${this.customer.dealerCompany}` : '');
          } else if (this.customer.dealerCurrencyBlocked) {
            text = this.t('dealerCurrency', { currency: this.customer.shopCurrency || '' });
          }
          dealer.hidden = !text;
          dealer.textContent = text;
        }

        const includedList = s('included');
        includedList.replaceChildren();
        if (stage) {
          stage.included.forEach((h) => {
            const p = this.catalog.product(h);
            if (!p) return;
            const li = Util.el('li', 'af-summary__item');
            li.appendChild(Util.el('span', 'af-summary__tick', '✓'));
            li.appendChild(Util.el('span', 'af-summary__name', p.title));
            if (p.backorder) li.appendChild(Util.el('span', 'af-summary__flag', this.t('backorder')));
            if (!p.available) li.appendChild(Util.el('span', 'af-summary__flag af-summary__flag--danger', this.t('outOfStock')));
            includedList.appendChild(li);
          });
        }
        s('includedGroup').hidden = !stage;

        const optList = s('options');
        optList.replaceChildren();
        (pricing ? pricing.optionLines : []).forEach((line) => {
          const li = Util.el('li', 'af-summary__item');
          li.appendChild(Util.el('span', 'af-summary__tick', '✓'));
          li.appendChild(Util.el('span', 'af-summary__name', line.title));
          li.appendChild(Util.el('span', 'af-summary__price', `+${this.money.format(line.price)}`));
          optList.appendChild(li);
        });
        s('optionsGroup').hidden = !(pricing && pricing.optionLines.length);

        const ownedList = s('owned');
        if (ownedList) {
          ownedList.replaceChildren();
          const reqs = this.catalog.requirementsFor(stage);
          reqs.forEach((r) => {
            const st = Rules.requirementState(this.catalog, stage, r, this.state.options, this.state.owned);
            if (st.kind !== 'own') return;
            const li = Util.el('li', 'af-summary__item');
            li.appendChild(Util.el('span', 'af-summary__tick', '✓'));
            li.appendChild(Util.el('span', 'af-summary__name', Rules.ownedLabel(this.catalog, st.own)));
            ownedList.appendChild(li);
          });
          const group = s('ownedGroup');
          if (group) group.hidden = !ownedList.children.length;
        }
        const fitEl = s('fitment');
        if (fitEl) {
          const fitTxt = stage ? this.fitText(Rules.stageFitment(this.catalog, stage)) : '';
          fitEl.textContent = fitTxt;
          fitEl.hidden = !fitTxt;
        }

        const lines = s('lines');
        lines.replaceChildren();
        if (pricing && stage) {
          const addLine = (label, value, cls) => {
            const row = Util.el('div', `af-summary__line${cls ? ` ${cls}` : ''}`);
            row.appendChild(Util.el('dt', null, label));
            row.appendChild(Util.el('dd', null, value));
            lines.appendChild(row);
          };
          if (pricing.mode === 'invalid') {
            addLine(this.t('packageLabel', { stage: stage.name }), this.t('priceUnavailable'));
          } else {
            addLine(this.t('packageLabel', { stage: stage.name }), this.money.format(pricing.packagePrice));
            if (pricing.mode === 'bundle' && pricing.savings > 0) {
              addLine(this.t('packageSavings'), this.money.format(pricing.savings), 'af-summary__line--muted');
            }
          }
          pricing.optionLines.forEach((l) => addLine(l.title, this.money.format(l.price)));
        }

        const total = s('total');
        total.textContent = totalText;
        total.classList.remove('af-skel');
        const msrp = s('msrp');
        const showMsrp = this.customer.dealer && this.customer.showMsrp && pricing && pricing.msrpTotal > pricing.total;
        msrp.hidden = !showMsrp;
        msrp.textContent = showMsrp ? `${this.t('msrp')} ${this.money.formatWithCurrency(pricing.msrpTotal)}` : '';

        const messages = s('messages');
        messages.replaceChildren();
        const items = [];
        if (validation) {
          const hideBasics = (e) => e.code === 'no-platform' || e.code === 'no-stage';
          validation.errors.filter((e) => !hideBasics(e)).forEach((e) => items.push(['error', e.text]));
          validation.warnings.forEach((w) => items.push(['warn', w.text]));
          if (!platform || !stage) items.push(['info', validation.errors[0] ? validation.errors[0].text : '']);
        }
        if (this.cartMessage) items.push([this.cartMessage.type, this.cartMessage.text]);
        items.filter((i) => i[1]).forEach(([type, text]) => {
          const p = Util.el('p', `af-msg af-msg--${type}`, text);
          messages.appendChild(p);
        });

        const add = Util.qs('[data-af-add]', root);
        const canAdd = !!(validation && validation.ok) && !this.state.busy;
        add.disabled = !canAdd;
        add.toggleAttribute('aria-busy', this.state.busy);
        Util.slot(root, 'addLabel').textContent = this.state.busy ? (this.busyLabel || this.t('adding')) : this.t('addToCart');
        const share = Util.qs('[data-af-share]', root);
        if (share) share.hidden = !stage;
      });

      if (this.dom.mobileTotal) this.dom.mobileTotal.textContent = totalText;
      if (this.dom.mobileLabel) this.dom.mobileLabel.textContent = machine;
      this.syncMobileBar();
    }

    syncMobileBar() {
      const bar = this.dom.mobileBar;
      if (!bar) return;
      const sheetOpen = !!(this.dom.sheet && this.dom.sheet.open);
      const visible = !!this.layoutVisible && !!this.state.platformKey && !sheetOpen;
      bar.toggleAttribute('data-visible', visible);
      bar.inert = !visible;
      if (this.dom.openSheet) this.dom.openSheet.setAttribute('aria-expanded', String(sheetOpen));
    }

    /* ---------- URL state ---------- */
    updateURL() {
      try {
        const url = new URL(window.location.href);
        const managed = ['platform', 'stage', 'options', 'have'].concat(this.legacyParams || []);
        managed.forEach((k) => url.searchParams.delete(k));
        const { platform, stage } = this.derived;
        const parts = [];
        const other = url.searchParams.toString();
        if (other) parts.push(other);
        if (platform) parts.push(`platform=${encodeURIComponent(platform.key)}`);
        if (platform && stage) {
          parts.push(`stage=${encodeURIComponent(stage.key)}`);
          const keys = Rules.optionHandles(this.catalog, platform, { includeRequirements: true })
            .filter((h) => this.state.options.has(h))
            .map((h) => encodeURIComponent((this.catalog.product(h) || {}).key || h));
          parts.push(`options=${keys.join(',')}`);
          const have = [];
          this.catalog.requirementsFor(stage).forEach((r) => {
            const own = this.state.owned.get(r.id);
            if (own) have.push(`${encodeURIComponent(r.key)}:${encodeURIComponent(own.key)}`);
          });
          if (have.length) parts.push(`have=${have.join(',')}`);
        }
        const next = `${url.pathname}${parts.length ? `?${parts.join('&')}` : ''}${url.hash}`;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (next !== current) window.history.replaceState(window.history.state, '', next);
        this.legacyParams = [];
      } catch (e) {
        console.warn('[AirforceBuilder] could not update URL', e);
      }
    }

    restoreConfigurationFromURL() {
      const params = new URLSearchParams(window.location.search);
      const problems = [];
      const platformParam = params.get('platform');
      const stageParam = params.get('stage');
      const hasOptionsParam = params.has('options');
      const optionKeys = (params.get('options') || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.legacyParams = [];
      // Also accept readable flags such as ?rp-pipe=1&ecu=1
      params.forEach((value, name) => {
        if (['platform', 'stage', 'options', 'have'].includes(name)) return;
        if (value === '1' && this.catalog.productByKey(name)) {
          optionKeys.push(name);
          this.legacyParams.push(name);
        }
      });
      const hasSharedState = !!(platformParam || stageParam || optionKeys.length);

      let platformKey = platformParam;
      if (!platformKey && this.catalog.platforms.length === 1) platformKey = this.catalog.platforms[0].key;
      if (!platformKey) {
        this.commit({ updateUrl: hasSharedState, announce: false });
        return;
      }
      const platform = this.catalog.platform(platformKey);
      if (!platform) {
        problems.push(this.t('urlPlatform', { key: platformKey }));
        this.showRestoreProblems(problems);
        this.commit({ updateUrl: true, announce: false });
        return;
      }

      this.applyPlatform(platform.key, { useDefaultStage: !stageParam });
      if (stageParam) {
        const stage = this.catalog.stageByKey(platform, stageParam);
        if (stage) {
          this.applyStage(stage.id, { applyDefaults: !hasOptionsParam && !optionKeys.length, announceDrops: false });
        } else {
          problems.push(this.t('urlStage', { key: stageParam }));
          if (platform.defaultStage) this.applyStage(platform.defaultStage, { applyDefaults: true, announceDrops: false });
        }
      }

      if (this.state.stageId && (hasOptionsParam || optionKeys.length)) {
        this.state.options = new Set();
        optionKeys.forEach((key) => {
          const product = this.catalog.productByKey(key);
          if (!product) { problems.push(this.t('urlOption', { key })); return; }
          const ctx = this.ctx();
          const status = Rules.optionStatus(this.catalog, ctx.platform, ctx.stage, product.handle, this.state.options);
          if (status.kind === 'included') return;
          if (status.kind !== 'available' || !this.applyToggle(product.handle, true)) {
            problems.push(`${product.title} (${Rules.describe(ctx, status, product.handle) || this.t('unavailable')})`);
          }
        });
      }
      if (this.state.stageId && params.get('have')) {
        const ctx = this.ctx();
        params.get('have').split(',').map((s) => s.trim()).filter(Boolean).forEach((pair) => {
          const [rk, ok] = pair.split(':').map((x) => String(x || '').toLowerCase());
          const req = this.catalog.requirementsFor(ctx.stage).find((r) => r.key === rk);
          const choice = req && Rules.requirementChoices(this.catalog, ctx.stage, req).own.find((c) => String(c.key).toLowerCase() === ok);
          if (!req || !choice || !this.applyRequirement(req.id, choice.value)) problems.push(this.t('urlOption', { key: pair }));
        });
      }
      this.pendingNotices = [];
      if (problems.length) this.showRestoreProblems(problems);
      if (hasSharedState) {
        this.markStarted('shared_link');
        this.analytics.track('platform_selected', { platform: platform.key, platform_name: platform.name, source: 'shared_link' });
      }
      this.commit({ updateUrl: true, announce: false });
    }

    showRestoreProblems(problems) {
      const box = this.dom.notices;
      if (!box) return;
      box.replaceChildren();
      const p = Util.el('p', 'af-notice__text', this.t('urlInvalid', { items: this.list(problems) }));
      const btn = Util.el('button', 'af-notice__dismiss', '×');
      btn.type = 'button';
      btn.setAttribute('data-af-dismiss-notices', '');
      btn.setAttribute('aria-label', 'Dismiss');
      box.append(p, btn);
      box.hidden = false;
    }

    /* ---------- cart ---------- */
    buildCartItems(buildId) {
      const { platform, stage } = this.derived;
      const products = Rules.cartProducts(this.catalog, stage, this.state.options);
      const label = this.settings.packageProperty;
      const ownedProps = {};
      this.catalog.requirementsFor(stage).forEach((r) => {
        const own = this.state.owned.get(r.id);
        if (own) ownedProps[r.name] = this.t('ownedProperty', { item: Rules.ownedLabel(this.catalog, own) });
      });
      return products.map((p, index) => {
        const role = p.isBundle ? 'package' : (stage.included.includes(p.handle) ? 'included' : 'option');
        const properties = {
          _af_build_id: buildId,
          _af_platform: platform.key,
          _af_stage: stage.key,
          _af_role: role
        };
        if (label) properties[label] = `${platform.name} — ${stage.name}`;
        // What the customer already has (so the right tune is supplied) — on the first line of the build.
        if (index === 0) Object.assign(properties, ownedProps);
        return { id: Number(p.variantId), quantity: p.qty, properties };
      });
    }

    snapshotLines() {
      const stage = this.catalog.stage(this.state.stageId);
      return Rules.cartProducts(this.catalog, stage, this.state.options)
        .map((p) => `${p.handle}:${p.variantId}:${p.price}:${p.available ? 1 : 0}`)
        .sort()
        .join('|');
    }

    async fetchFreshData() {
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Refresh failed (${res.status}).`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('[data-af-data]');
      if (!script) throw new Error('Refresh returned no builder data.');
      return JSON.parse(script.textContent);
    }

    /** Re-reads live Shopify prices/inventory. Returns false if the customer must review. */
    async revalidate() {
      let fresh;
      try {
        fresh = await this.fetchFreshData();
      } catch (e) {
        console.warn('[AirforceBuilder] live revalidation skipped:', e.message);
        return true; // Shopify's cart still rejects unavailable stock; prices are always Shopify's.
      }
      const before = this.snapshotLines();
      this.loadData(fresh);
      this.renderedStagesFor = null;
      this.renderedOptionsFor = null;
      this.renderedReqFor = null;
      const platform = this.catalog.platform(this.state.platformKey);
      const stage = this.catalog.stage(this.state.stageId);
      if (!platform || !stage || !platform.stages.includes(stage.id)) {
        this.state.stageId = null;
        this.state.options = new Set();
      } else {
        const ctx = Object.assign(this.ctx(), { platform, stage });
        const result = Rules.carryOver(ctx, this.state.options, this.state.userRemoved, false, true);
        this.state.options = result.options;
        this.pendingNotices.push(...result.notices);
      }
      const after = this.snapshotLines();
      this.pendingNotices = [];
      this.commit({ announce: false });
      if (before !== after) {
        this.cartMessage = { type: 'warn', text: this.t('pricesChanged') };
        this.updateSummary();
        this.flash(this.t('pricesChanged'), 'warn');
        return false;
      }
      return !!(this.derived.validation && this.derived.validation.ok);
    }

    setBusy(busy, label) {
      this.state.busy = busy;
      this.busyLabel = label || null;
      this.root.toggleAttribute('data-busy', busy);
      this.updateSummary();
    }

    async addBuildToCart() {
      if (this.state.busy) return;
      const validation = this.validateConfiguration();
      if (!validation.ok) {
        this.flash(validation.errors[0].text, 'error');
        return;
      }
      const buildId = Util.uid();
      this.cartMessage = null;
      try {
        this.setBusy(true, this.t('checking'));
        const ok = await this.revalidate();
        if (!ok) { this.setBusy(false); return; }
        this.setBusy(true, this.t('adding'));
        const items = this.buildCartItems(buildId);
        if (!items.length || items.some((i) => !i.id)) throw new Error(this.t('stageMisconfigured'));
        let response;
        try {
          response = await this.cart.add(items);
        } catch (error) {
          await this.rollback(buildId, error);
          return;
        }
        const added = Array.isArray(response.items) ? response.items : [];
        const missing = items.filter((i) => !added.some((r) => Number(r.variant_id || r.id) === i.id));
        if (missing.length) {
          await this.rollback(buildId, new Error(''));
          return;
        }
        const { platform, stage, pricing } = this.derived;
        this.analytics.track('build_added_to_cart', {
          platform: platform.key,
          stage: stage.key,
          build_id: buildId,
          options: Array.from(this.state.options),
          total_cents: pricing.total,
          pricing_mode: pricing.mode,
          items: items.map((i) => ({ variant_id: i.id, quantity: i.quantity }))
        });
        document.dispatchEvent(new CustomEvent('airforce:cart-updated', { detail: { buildId, items: added } }));
        this.cartMessage = { type: 'success', text: this.t('addedToCart') };
        this.announce(this.t('addedToCart'));
        if (this.settings.afterAdd === 'cart') {
          window.location.assign(this.cart.cartUrl);
          return;
        }
        this.setBusy(false);
        this.flash(this.t('addedToCart'), 'success');
      } catch (error) {
        console.error('[AirforceBuilder] add to cart failed', error);
        this.cartMessage = { type: 'error', text: this.t('cartError', { reason: error.message || '' }) };
        this.setBusy(false);
        this.flash(this.cartMessage.text, 'error');
      }
    }

    async rollback(buildId, error) {
      let clean = true;
      try {
        await this.cart.removeBuild(buildId);
      } catch (e) {
        clean = false;
        console.error('[AirforceBuilder] rollback failed', e);
      }
      const reason = error && error.message ? error.message : '';
      this.cartMessage = { type: 'error', text: this.t(clean ? 'cartError' : 'cartRollbackFailed', { reason }) };
      this.setBusy(false);
      this.flash(this.cartMessage.text, 'error');
      // Refresh availability so the UI reflects why it failed.
      try {
        const fresh = await this.fetchFreshData();
        this.loadData(fresh);
        this.renderedStagesFor = null;
        this.renderedOptionsFor = null;
        this.commit({ announce: false });
      } catch (e) { /* keep current view */ }
    }

    /* ---------- UI helpers ---------- */
    setImage(img, image, widths, sizes) {
      if (!img) return;
      const wrap = img.closest('[data-media]');
      if (!image || !image.src) {
        img.hidden = true;
        img.removeAttribute('src');
        img.removeAttribute('srcset');
        if (wrap) wrap.classList.add('is-empty');
        return;
      }
      if (wrap) wrap.classList.remove('is-empty');
      img.hidden = false;
      img.sizes = sizes;
      img.srcset = Util.srcset(image.src, widths);
      img.src = Util.imageUrl(image.src, widths[Math.min(1, widths.length - 1)]);
      img.alt = image.alt || '';
      if (image.w && image.h) { img.width = image.w; img.height = image.h; }
    }

    flash(text, type = 'info') {
      if (!text) return;
      const toast = this.dom.toast;
      if (toast) {
        toast.textContent = text;
        toast.dataset.tone = type;
        toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
        toast.hidden = false;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => { toast.hidden = true; }, type === 'error' ? 9000 : 5000);
      } else {
        this.announce(text);
      }
    }

    announce(text) {
      if (!this.dom.live) return;
      this.dom.live.textContent = '';
      const later = window.requestAnimationFrame || ((fn) => setTimeout(fn, 16));
      later(() => { this.dom.live.textContent = text; });
    }

    announceTotal() {
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        const { platform, stage, pricing } = this.derived;
        if (!platform || !stage || !pricing || pricing.mode === 'invalid') return;
        this.announce(`${platform.name} ${stage.name}. ${this.t('total')} ${this.money.formatWithCurrency(pricing.total)}.`);
      }, 500);
    }

    markStarted(source) {
      if (this.started) return;
      this.started = true;
      this.analytics.track('builder_started', { source });
    }

    scrollIntoView(node) {
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight * 0.6) return;
      node.scrollIntoView({ behavior: Util.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    openSheet() {
      const sheet = this.dom.sheet;
      if (!sheet) return;
      if (typeof sheet.showModal === 'function') sheet.showModal();
      else sheet.setAttribute('open', '');
      this.syncMobileBar();
      const heading = Util.qs('[data-af-summary] h2', sheet);
      if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus(); }
    }

    closeSheet() {
      const sheet = this.dom.sheet;
      if (!sheet || !sheet.open) return;
      this.closeDialog(sheet);
      this.syncMobileBar();
      if (this.dom.openSheet && !window.matchMedia(DESKTOP_QUERY).matches) this.dom.openSheet.focus();
    }

    closeDialog(dialog) {
      if (!dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    openLightbox(which) {
      const d = this.derived.dyno;
      const box = this.dom.lightbox;
      if (!box || !d || !d.run) return;
      const image = which === 'comparison' ? d.run.comparison : d.run.graph;
      if (!image || !image.src) return;
      const label = this.describeConfiguration(this.derived.platform, this.derived.stage, d.tested);
      this.dom.lightboxImg.src = Util.imageUrl(image.src, 2000);
      this.dom.lightboxImg.alt = image.alt || label;
      this.dom.lightboxCaption.textContent = label;
      if (typeof box.showModal === 'function') box.showModal(); else box.setAttribute('open', '');
      const close = Util.qs('[data-af-close-lightbox]', box);
      if (close) close.focus();
    }

    async shareBuild() {
      this.updateURL();
      const url = window.location.href;
      const { platform, stage } = this.derived;
      const title = platform && stage ? `${platform.name} — ${stage.name}` : document.title;
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (coarse && navigator.share) {
        try { await navigator.share({ title, url }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
      }
      try {
        await navigator.clipboard.writeText(url);
        this.flash(this.t('linkCopied'), 'success');
      } catch (e) {
        this.flash(this.t('linkCopyFailed', { url }), 'info');
      }
    }

    showFatal() {
      this.root.dataset.state = 'error';
      const fatal = Util.qs('[data-af-fatal]', this.root);
      if (fatal) fatal.hidden = false;
    }
  }

  /* ------------------------------------------------------------------ *
   * Boot + Theme Editor support
   * ------------------------------------------------------------------ */
  const instances = new Map();

  function initAll(scope = document) {
    Util.qsa('[data-af-builder]', scope).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new AirforceBuilder(root));
    });
  }

  function destroyWithin(scope) {
    Array.from(instances.keys()).forEach((root) => {
      if (scope === root || scope.contains(root)) {
        instances.get(root).destroy();
        instances.delete(root);
      }
    });
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroyWithin(e.target));

  window.AirforceBuilder = {
    version: VERSION,
    get(root) { return instances.get(root || Util.qs('[data-af-builder]')) || null; },
    _internals: { Rules, Pricing, Dyno, createCatalog }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
})();